import { EllipsisHorizontal, MapPin, Plus, TruckFast } from "@medusajs/icons"
import { Badge, Button, Checkbox, Drawer, IconButton, Input, Prompt, Switch, Text, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { Field } from "@/components/form"
import { EmptyState, ErrorState, SaveBar, SearchInput, Section } from "@/components/ui"
import { formatMoney } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { SettingsGroup, SettingsTitle } from "./settings-general"
import { CountryList, CountryPicker, flag } from "@/components/country-picker"
import { AddressFields, addressLine, addressProblem, type AddressValue } from "@/components/address-fields"
import { countryName } from "@/lib/geo"
import { summarizeCountries, useMarkets } from "@/lib/markets"
import { Badge as TbBadge, Banner } from "@/terabizz"
import { ArrowLeft } from "@medusajs/icons"
import { Link, useParams } from "react-router-dom"
import { ShipStationCard } from "@/components/shipstation"

// ── Shipping and delivery ──────────────────────────────────────────────────

type Rate = { id: string; zone_id: string; name: string; description: string; price: number; free_over: number | null; active: boolean }
type Zone = { id: string; name: string; location: string | null; countries: string[] }

function useShipping() {
  return useQuery({ queryKey: ["settings", "shipping"], queryFn: () => sdk.client.fetch<{ zones: Zone[]; rates: Rate[] }>("/admin/terify/shipping") })
}

export function ShippingSettingsPage() {
  const { data, error } = useShipping()
  const markets = useMarkets().data
  const canWrite = useCan()("settings:write")
  const [editing, setEditing] = useState<Partial<Rate> | null>(null)
  const [zone, setZone] = useState<Partial<Zone> | null>(null)
  if (error) return <ErrorState error={error} />
  if (!data) return null
  const selling = (markets ?? []).filter((m) => m.status === "active").flatMap((m) => m.countries)
  const covered = new Set(data.zones.flatMap((z) => z.countries.map((c) => c.toLowerCase())))
  const missing = markets ? selling.filter((c) => !covered.has(c)) : []
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <SettingsTitle title="Shipping and delivery" subtitle="Choose where you ship and how much you charge for shipping at checkout." />
        {canWrite && <Button size="small" className="shrink-0 whitespace-nowrap" onClick={() => setZone({ countries: [] })}>Create zone</Button>}
      </div>
      {missing.length > 0 && (
        <div className="mb-4">
          <Banner tone="warning" title={`${missing.length} countr${missing.length === 1 ? "y/region" : "ies/regions"} in your markets can't be shipped to`} action={canWrite ? <button type="button" className="underline" onClick={() => setZone({ countries: missing })}>Create a zone for {missing.length === 1 ? "it" : "them"}</button> : undefined}>
            {summarizeCountries(missing.map(countryName))}. Customers there can't check out until a zone includes {missing.length === 1 ? "it" : "them"}.
          </Banner>
        </div>
      )}
      <SettingsGroup title="Shipping zones" description="Each zone is a group of countries/regions with its own rates. Customers see the rates of the zone their address is in.">
        <div className="flex flex-col gap-y-4">
          {data.zones.map((z) => {
            const rates = data.rates.filter((r) => r.zone_id === z.id)
            return (
              <Section key={z.id}>
                <div className="flex items-start gap-x-3 px-4 py-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-ui-bg-component"><TruckFast /></span>
                  <div className="min-w-0 flex-1">
                    <Text size="small" weight="plus">{z.name}</Text>
                    <Text size="xsmall" className="text-ui-fg-subtle">{summarizeCountries(z.countries.map(countryName))}{z.location ? ` · ships from ${z.location}` : ""}</Text>
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 items-center gap-x-2">
                      <Button size="small" variant="secondary" onClick={() => setEditing({ zone_id: z.id, active: true, free_over: null })}>
                        <Plus /> Add rate
                      </Button>
                      <IconButton size="small" variant="transparent" aria-label={`Edit zone ${z.name}`} onClick={() => setZone(z)}>
                        <EllipsisHorizontal />
                      </IconButton>
                    </div>
                  )}
                </div>
                {rates.length ? (
                  <ul className="divide-y">
                    {rates.map((r) => (
                      <li key={r.id} className="flex items-center gap-x-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-x-2">
                            <Text size="small" weight="plus">{r.name}</Text>
                            {!r.active && <Badge size="2xsmall">Hidden</Badge>}
                          </div>
                          <Text size="xsmall" className="text-ui-fg-subtle">
                            {[r.description, r.free_over != null ? `Free on orders over ${formatMoney(r.free_over)}` : null].filter(Boolean).join(" · ")}
                          </Text>
                        </div>
                        <Text size="small">{r.price === 0 ? "Free" : formatMoney(r.price)}</Text>
                        {canWrite && (
                          <IconButton size="small" variant="transparent" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)}>
                            <EllipsisHorizontal />
                          </IconButton>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState title="No rates" body="Customers in this zone can't check out until you add a rate." />
                )}
              </Section>
            )
          })}
          {!data.zones.length && <Section><EmptyState title="No shipping zones" body="Create a zone to choose where you ship." /></Section>}
        </div>
      </SettingsGroup>
      <SettingsGroup title="Shipping apps" description="Print labels and ship orders from another app. Shipments made there come back here with tracking.">
        <ShipStationCard />
      </SettingsGroup>
      {editing && <RateDrawer rate={editing} onClose={() => setEditing(null)} />}
      {zone && <ZoneDrawer zone={zone} zones={data.zones} marketCountries={(markets ?? []).flatMap((m) => m.countries)} last={data.zones.length <= 1} onClose={() => setZone(null)} />}
    </>
  )
}

function ZoneDrawer({ zone, zones, marketCountries, last, onClose }: { zone: Partial<Zone>; zones: Zone[]; marketCountries: string[]; last: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(zone.name ?? "")
  const [countries, setCountries] = useState<string[]>((zone.countries ?? []).map((c) => c.toLowerCase()))
  const [picking, setPicking] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const taken = useMemo(() => Object.fromEntries(zones.filter((z) => z.id !== zone.id).flatMap((z) => z.countries.map((c) => [c.toLowerCase(), `${z.name} zone`]))), [zones, zone.id])
  // Zones cover countries the store sells to; keep any country the zone already has.
  const only = useMemo(() => [...new Set([...marketCountries, ...countries])], [marketCountries, countries])
  const done = () => (qc.invalidateQueries({ queryKey: ["settings", "shipping"] }), onClose())
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), countries }
      return zone.id ? sdk.client.fetch(`/admin/terify/shipping/zones/${zone.id}`, { method: "POST", body }) : sdk.client.fetch("/admin/terify/shipping/zones", { method: "POST", body })
    },
    onSuccess: () => (toast.success(zone.id ? "Zone saved" : "Zone created"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/shipping/zones/${zone.id}`, { method: "DELETE" }),
    onSuccess: () => (toast.success("Zone deleted"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{zone.id ? "Edit zone" : "Create zone"}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <Field label="Zone name" hint="Customers won't see this, e.g. Europe, Domestic, International."><Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} /></Field>
          <div className="flex flex-col gap-y-2">
            <div className="flex items-center justify-between">
              <Text size="small" weight="plus">Countries/regions</Text>
              <Button type="button" size="small" variant="secondary" onClick={() => setPicking(true)}>{countries.length ? "Edit" : "Add"}</Button>
            </div>
            <CountryList codes={countries} />
            {!marketCountries.length && <Text size="xsmall" className="text-ui-fg-muted">Add countries/regions to a market first.</Text>}
          </div>
        </Drawer.Body>
        <Drawer.Footer>
          {zone.id && !last && <Button variant="secondary" size="small" className="mr-auto text-ui-fg-error" onClick={() => setConfirm(true)}>Delete zone</Button>}
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" isLoading={save.isPending} disabled={!name.trim() || !countries.length} onClick={() => save.mutate()}>Done</Button>
        </Drawer.Footer>
      </Drawer.Content>
      {picking && <CountryPicker title="Countries/regions in this zone" selected={countries} taken={taken} only={only} onClose={() => setPicking(false)} onDone={setCountries} />}
      <Prompt open={confirm} onOpenChange={setConfirm}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {zone.name}?</Prompt.Title>
            <Prompt.Description>Its rates are deleted too. Customers in these countries/regions won't be able to check out until another zone includes them.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate()}>Delete zone</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Drawer>
  )
}

function RateDrawer({ rate, onClose }: { rate: Partial<Rate>; onClose: () => void }) {
  const qc = useQueryClient()
  const [v, setV] = useState({
    name: rate.name ?? "",
    description: rate.description ?? "",
    price: rate.price != null ? String(rate.price) : "",
    free: rate.free_over != null,
    free_over: rate.free_over != null ? String(rate.free_over) : "",
    active: rate.active ?? true,
  })
  const [confirm, setConfirm] = useState(false)
  const done = () => (qc.invalidateQueries({ queryKey: ["settings", "shipping"] }), onClose())
  const save = useMutation({
    mutationFn: () => {
      const body = { zone_id: rate.zone_id, name: v.name.trim(), description: v.description.trim(), price: Number(v.price || 0), free_over: v.free ? Number(v.free_over) : null, active: v.active }
      return rate.id ? sdk.client.fetch(`/admin/terify/shipping/rates/${rate.id}`, { method: "POST", body }) : sdk.client.fetch("/admin/terify/shipping/rates", { method: "POST", body })
    },
    onSuccess: () => (toast.success("Rate saved"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/shipping/rates/${rate.id}`, { method: "DELETE" }),
    onSuccess: () => (toast.success("Rate deleted"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{rate.id ? "Edit rate" : "Add rate"}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <Field label="Rate name" hint="Customers see this at checkout, e.g. Standard, Express"><Input value={v.name} maxLength={80} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
          <Field label="Transit time" hint="e.g. 3–5 business days"><Input value={v.description} maxLength={120} onChange={(e) => setV({ ...v, description: e.target.value })} /></Field>
          <Field label="Price (USD)"><Input inputMode="decimal" value={v.price} placeholder="0.00" onChange={(e) => setV({ ...v, price: e.target.value.replace(/[^0-9.]/g, "") })} /></Field>
          <label className="flex items-center gap-x-2">
            <Checkbox checked={v.free} onCheckedChange={(c) => setV({ ...v, free: c === true })} />
            <Text size="small">Free when the order subtotal is over an amount</Text>
          </label>
          {v.free && <Field label="Minimum order subtotal (USD)"><Input inputMode="decimal" value={v.free_over} onChange={(e) => setV({ ...v, free_over: e.target.value.replace(/[^0-9.]/g, "") })} /></Field>}
          <label className="flex items-center gap-x-2">
            <Switch checked={v.active} onCheckedChange={(c) => setV({ ...v, active: c })} />
            <Text size="small">Show this rate at checkout</Text>
          </label>
        </Drawer.Body>
        <Drawer.Footer>
          {rate.id && <Button variant="secondary" size="small" className="mr-auto text-ui-fg-error" onClick={() => setConfirm(true)}>Delete</Button>}
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" isLoading={save.isPending} disabled={!v.name.trim() || (v.free && !(Number(v.free_over) > 0))} onClick={() => save.mutate()}>Done</Button>
        </Drawer.Footer>
      </Drawer.Content>
      <Prompt open={confirm} onOpenChange={setConfirm}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {rate.name}?</Prompt.Title>
            <Prompt.Description>Customers won't see this rate at checkout any more.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate()}>Delete rate</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Drawer>
  )
}

// ── Taxes and duties ───────────────────────────────────────────────────────

type TaxRow = { code: string; name: string; collecting: boolean; rate: number | null }
type CountryTax = TaxRow & { market_id: string | null; market_name: string | null; tax_name: string; has_provinces: boolean; provinces: TaxRow[] }

function useTaxes() {
  return useQuery({ queryKey: ["settings", "taxes"], queryFn: () => sdk.client.fetch<{ countries: CountryTax[] }>("/admin/terify/taxes") })
}

const taxSummary = (c: CountryTax) => {
  const prov = c.provinces.filter((p) => p.collecting).length
  const parts = [c.collecting ? `Collecting ${c.rate}% ${c.tax_name}` : null, prov ? `${prov} ${c.code === "US" ? "state" : "regional"} rate${prov === 1 ? "" : "s"}` : null].filter(Boolean)
  return parts.length ? parts.join(" · ") : "Not collecting"
}

/** Settings → Taxes and duties: one row per country/region you sell to, grouped by market. */
export function TaxSettingsPage() {
  const { data, error } = useTaxes()
  const markets = useMarkets().data
  if (error) return <ErrorState error={error} />
  if (!data || !markets) return null
  const groups = [
    ...markets.map((m) => ({ key: m.id, title: m.name, note: m.tax_inclusive ? "Prices include tax" : "Tax is added at checkout", rows: data.countries.filter((c) => c.market_id === m.id) })),
    { key: "none", title: "Not in a market", note: "Rates set earlier for countries/regions you no longer sell to.", rows: data.countries.filter((c) => !c.market_id) },
  ].filter((g) => g.rows.length)
  return (
    <>
      <SettingsTitle title="Taxes and duties" subtitle="Charge VAT, GST or sales tax in the countries/regions where you're registered to collect it." />
      <div className="mb-4">
        <Banner tone="info">Tax rates are applied at checkout and on draft orders. Check which countries/regions you must collect in with a tax professional.</Banner>
      </div>
      {groups.map((g) => (
        <SettingsGroup key={g.key} title={g.title} description={g.note}>
          <Section>
            <ul className="divide-y">
              {g.rows.map((c) => (
                <li key={c.code}>
                  <Link to={`/settings/taxes/${c.code.toLowerCase()}`} className="flex items-center gap-x-3 px-4 py-3 hover:bg-ui-bg-base-hover">
                    <span aria-hidden className="text-[18px] leading-none">{flag(c.code)}</span>
                    <div className="min-w-0 flex-1">
                      <Text size="small" weight="plus">{c.name}</Text>
                      <Text size="xsmall" className="text-ui-fg-subtle">{taxSummary(c)}</Text>
                    </div>
                    <TbBadge tone={c.collecting || c.provinces.some((p) => p.collecting) ? "success" : "neutral"}>{c.collecting || c.provinces.some((p) => p.collecting) ? "Collecting" : "Not collecting"}</TbBadge>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        </SettingsGroup>
      ))}
      <SettingsGroup title="Duties and import taxes">
        <Section>
          <div className="px-4 py-3">
            <Text size="small">Customers pay duties and import taxes to the carrier when the order is delivered. They aren't collected at checkout.</Text>
            <Text size="xsmall" className="mt-1 text-ui-fg-subtle">
              To warn customers at checkout, turn on the duties notice for a market in <Link to="/settings/markets" className="text-ui-fg-interactive">Markets</Link>.
            </Text>
          </div>
        </Section>
      </SettingsGroup>
    </>
  )
}

/** Settings → Taxes and duties → a country/region: the country rate and state/province rates. */
export function TaxCountryPage() {
  const { cc = "" } = useParams()
  const { data, error } = useTaxes()
  if (error) return <ErrorState error={error} />
  if (!data) return null
  const c = data.countries.find((x) => x.code === cc.toUpperCase())
  if (!c) return <EmptyState title="Country/region not found" body="Add it to a market first." action={<Button size="small" variant="secondary" asChild><Link to="/settings/taxes">Back to taxes</Link></Button>} />
  return <TaxCountryForm key={c.code + String(c.rate) + String(c.collecting)} c={c} />
}

function TaxCountryForm({ c }: { c: CountryTax }) {
  const qc = useQueryClient()
  const canWrite = useCan()("settings:write")
  const [collecting, setCollecting] = useState(c.collecting)
  const [rate, setRate] = useState(c.rate != null ? String(c.rate) : "")
  const [q, setQ] = useState("")
  const [editing, setEditing] = useState<TaxRow | null>(null)
  const dirty = collecting !== c.collecting || (collecting && rate !== (c.rate != null ? String(c.rate) : ""))
  const r = Number(rate)
  const valid = !collecting || (rate !== "" && r >= 0 && r <= 50)
  const save = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/taxes/countries/${c.code.toLowerCase()}`, { method: "POST", body: collecting ? { collecting: true, rate: r } : { collecting: false } }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["settings", "taxes"] }), toast.success(collecting ? `Collecting ${c.tax_name} in ${c.name}` : `Stopped collecting ${c.tax_name} in ${c.name}`)),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const provinces = c.provinces.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase() === q.toLowerCase())
  const isUS = c.code === "US"
  return (
    <div className="pb-24">
      <div className="mb-6 flex items-center gap-x-2">
        <Link to="/settings/taxes" aria-label="Back to taxes" className="flex size-7 items-center justify-center rounded-md hover:bg-ui-bg-base-hover"><ArrowLeft /></Link>
        <span aria-hidden className="text-[20px] leading-none">{flag(c.code)}</span>
        <h1 className="k-h1">{c.name}</h1>
      </div>
      <div className="flex flex-col gap-y-4">
        <Section title={isUS ? "Federal tax" : `${c.tax_name}`}>
          <label className="flex items-start justify-between gap-x-4 px-4 py-3">
            <div>
              <Text size="small" weight="plus">{isUS ? "Collect a country-wide rate" : `Collect ${c.tax_name} in ${c.name}`}</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">
                {isUS ? "The US has no federal sales tax. Set rates per state below." : `Applied to every order shipped to ${c.name}${c.has_provinces ? ", unless a state/province rate below replaces it" : ""}.`}
              </Text>
            </div>
            <Switch checked={collecting} disabled={!canWrite} onCheckedChange={setCollecting} />
          </label>
          {collecting && (
            <div className="px-4 py-3">
              <Field label="Tax rate (%)" hint="0–50%. Use the standard rate for the products you sell.">
                <Input inputMode="decimal" value={rate} disabled={!canWrite} onChange={(e) => setRate(e.target.value.replace(/[^0-9.]/g, ""))} />
              </Field>
            </div>
          )}
        </Section>
        {c.has_provinces && (
          <Section title={isUS ? "Sales tax by state" : "Regional rates"}>
            <div className="px-4 py-3">
              <Text size="xsmall" className="text-ui-fg-subtle">
                {isUS ? "Turn on collection for each state where you have nexus. The rate is the combined state + local rate." : "A regional rate replaces the country rate for addresses in that state/province (for example HST in Ontario)."}
              </Text>
            </div>
            <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder={isUS ? "Search states" : "Search states/provinces"} /></div>
            <ul className="divide-y">
              {provinces.map((p) => (
                <li key={p.code} className="flex items-center justify-between gap-x-3 px-4 py-2.5">
                  <div>
                    <Text size="small" weight="plus">{p.name}</Text>
                    <Text size="xsmall" className="text-ui-fg-subtle">{p.collecting ? `Collecting at ${p.rate}%` : "Not collecting"}</Text>
                  </div>
                  {canWrite && <Button size="small" variant="secondary" onClick={() => setEditing(p)}>{p.collecting ? "Edit" : "Collect tax"}</Button>}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
      {canWrite && <SaveBar visible={dirty} message="Unsaved changes" saving={save.isPending} disabled={!valid} onDiscard={() => (setCollecting(c.collecting), setRate(c.rate != null ? String(c.rate) : ""))} onSave={() => save.mutate()} />}
      {editing && <ProvinceTaxDrawer country={c} province={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function ProvinceTaxDrawer({ country, province, onClose }: { country: CountryTax; province: TaxRow; onClose: () => void }) {
  const qc = useQueryClient()
  const [rate, setRate] = useState(province.rate != null ? String(province.rate) : "")
  const label = country.code === "US" ? "sales tax" : country.tax_name
  const mut = useMutation({
    mutationFn: (body: { collecting: boolean; rate?: number }) => sdk.client.fetch(`/admin/terify/taxes/countries/${country.code.toLowerCase()}/provinces/${province.code}`, { method: "POST", body }),
    onSuccess: (_d, v) => (qc.invalidateQueries({ queryKey: ["settings", "taxes"] }), toast.success(v.collecting ? `Collecting ${label} in ${province.name}` : `Stopped collecting in ${province.name}`), onClose()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const r = Number(rate)
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{province.name} {label}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Tax rate (%)" hint={country.code === "US" ? "Combined state + local rate applied to orders shipped to this state." : `Replaces the ${country.name} rate for orders shipped to ${province.name}.`}>
            <Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value.replace(/[^0-9.]/g, ""))} />
          </Field>
        </Drawer.Body>
        <Drawer.Footer>
          {province.collecting && <Button variant="secondary" size="small" className="mr-auto" isLoading={mut.isPending} onClick={() => mut.mutate({ collecting: false })}>Stop collecting</Button>}
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!(r >= 0 && r <= 50) || rate === ""} isLoading={mut.isPending} onClick={() => mut.mutate({ collecting: true, rate: r })}>Save</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

// ── Locations ──────────────────────────────────────────────────────────────

function useLocations() {
  return useQuery({
    queryKey: ["settings", "locations"],
    queryFn: async () => {
      const [{ stock_locations }, { stores }] = await Promise.all([
        sdk.admin.stockLocation.list({ limit: 50, fields: "id,name,*address,*sales_channels" } as any),
        sdk.admin.store.list({ fields: "id,default_location_id,default_sales_channel_id" } as any),
      ])
      return { locations: stock_locations as any[], store: stores[0] as any }
    },
  })
}

export function LocationsSettingsPage() {
  const { data, error } = useLocations()
  const canWrite = useCan()("settings:write")
  const [editing, setEditing] = useState<any | null>(null)
  if (error) return <ErrorState error={error} />
  if (!data) return null
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <SettingsTitle title="Locations" subtitle="Places where you stock inventory and fulfill orders." />
        {canWrite && <Button size="small" className="shrink-0 whitespace-nowrap" onClick={() => setEditing({})}>Add location</Button>}
      </div>
      <Section>
        <ul className="divide-y">
          {data.locations.map((l) => {
            const online = (l.sales_channels ?? []).some((c: any) => c.id === data.store?.default_sales_channel_id)
            const a = l.address ?? {}
            return (
              <li key={l.id}>
                <button disabled={!canWrite} onClick={() => setEditing(l)} className="flex w-full items-center gap-x-3 px-4 py-3 text-left hover:bg-ui-bg-base-hover disabled:hover:bg-transparent">
                  <span className="flex size-9 items-center justify-center rounded-lg bg-ui-bg-component"><MapPin /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Text size="small" weight="plus">{l.name}</Text>
                      {data.store?.default_location_id === l.id && <Badge size="2xsmall">Default</Badge>}
                      {!online && <Badge size="2xsmall" color="orange">Not fulfilling online orders</Badge>}
                    </div>
                    <Text size="xsmall" className="truncate text-ui-fg-subtle">{addressLine(a) || "No address"}</Text>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      </Section>
      {editing && <LocationDrawer location={editing} store={data.store} onClose={() => setEditing(null)} />}
    </>
  )
}

function LocationDrawer({ location, store, onClose }: { location: any; store: any; onClose: () => void }) {
  const qc = useQueryClient()
  const a = location.address ?? {}
  const online0 = (location.sales_channels ?? []).some((c: any) => c.id === store?.default_sales_channel_id)
  const [v, setV] = useState({
    name: location.name ?? "",
    online: location.id ? online0 : true,
    isDefault: store?.default_location_id === location.id,
  })
  const [addr, setAddr] = useState<AddressValue>({
    address_1: a.address_1 ?? "",
    address_2: a.address_2 ?? "",
    city: a.city ?? "",
    province: a.province ?? "",
    postal_code: a.postal_code ?? "",
    phone: a.phone ?? "",
    country_code: (a.country_code ?? "us").toLowerCase(),
  })
  const [confirm, setConfirm] = useState(false)
  const done = () => (qc.invalidateQueries({ queryKey: ["settings", "locations"] }), qc.invalidateQueries({ queryKey: ["store-context"] }), onClose())
  const save = useMutation({
    mutationFn: async () => {
      const problem = addressProblem(addr)
      if (problem) throw new Error(problem.message)
      const t = (x?: string) => x?.trim() || undefined
      const address = { address_1: addr.address_1!.trim(), address_2: t(addr.address_2), city: addr.city!.trim(), province: t(addr.province), postal_code: t(addr.postal_code), phone: t(addr.phone), country_code: addr.country_code!.toUpperCase() }
      const id = location.id
        ? (await sdk.admin.stockLocation.update(location.id, { name: v.name.trim(), address } as any)).stock_location.id
        : (await sdk.admin.stockLocation.create({ name: v.name.trim(), address } as any)).stock_location.id
      if (!location.id) await sdk.client.fetch(`/admin/stock-locations/${id}/fulfillment-providers`, { method: "POST", body: { add: ["manual_manual"] } }).catch(() => undefined)
      if (store?.default_sales_channel_id && v.online !== online0) {
        await sdk.client.fetch(`/admin/stock-locations/${id}/sales-channels`, { method: "POST", body: v.online ? { add: [store.default_sales_channel_id] } : { remove: [store.default_sales_channel_id] } })
      }
      if (v.isDefault && store?.default_location_id !== id) await sdk.admin.store.update(store.id, { default_location_id: id } as any)
    },
    onSuccess: () => (toast.success("Location saved"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.admin.stockLocation.delete(location.id),
    onSuccess: () => (toast.success("Location deleted"), done()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const f = (k: "name") => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{location.id ? location.name : "Add location"}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <Field label="Location name" hint="Give this location a short name to make it easy to identify."><Input value={v.name} maxLength={80} onChange={f("name")} /></Field>
          <AddressFields value={addr} onChange={setAddr} name={false} company={false} />
          <label className="flex items-start gap-x-2">
            <Checkbox className="mt-0.5" checked={v.online} onCheckedChange={(c) => setV({ ...v, online: c === true })} />
            <div>
              <Text size="small">Fulfill online orders from this location</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">Inventory at this location is available for sale online.</Text>
            </div>
          </label>
          <label className="flex items-center gap-x-2">
            <Checkbox checked={v.isDefault} disabled={store?.default_location_id === location.id} onCheckedChange={(c) => setV({ ...v, isDefault: c === true })} />
            <Text size="small">Default location (new products' stock goes here)</Text>
          </label>
        </Drawer.Body>
        <Drawer.Footer>
          {location.id && store?.default_location_id !== location.id && (
            <Button variant="secondary" size="small" className="mr-auto" onClick={() => setConfirm(true)}>Delete</Button>
          )}
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" isLoading={save.isPending} disabled={!v.name.trim() || !addr.address_1?.trim() || !addr.city?.trim()} onClick={() => save.mutate()}>Save</Button>
        </Drawer.Footer>
      </Drawer.Content>
      <Prompt open={confirm} onOpenChange={setConfirm}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {location.name}?</Prompt.Title>
            <Prompt.Description>Stock levels at this location are removed. Orders already fulfilled from it aren't affected.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate()}>Delete location</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Drawer>
  )
}

